import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EDUCATION_TYPE_LABELS, EDUCATION_TYPES, type EducationType } from '@hygge/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/errors';
import { useCreateEducation, useUpdateEducation, type Education } from '@/lib/employee-finance';
import { serverFieldErrors } from '@/features/people/lib/server-field-errors';
import { invalidProps, withoutFieldError } from '@/features/people/lib/field-a11y';
import { useFocusFirstError } from '@/features/people/lib/use-focus-first-error';
import { FieldError } from './field-error';
import {
  EDUCATION_SERVER_FIELDS,
  toEducationForm,
  toEducationInput,
  validateEducation,
  type EducationErrors,
  type EducationForm,
} from '@/features/people/lib/education-form';

interface EducationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  /** The entry to edit; null adds a new one. */
  entry: Education | null;
}

/** Form keys and their element ids, in the order the form shows them. */
const FIELD_IDS = [
  ['type', 'education-type'],
  ['institution', 'education-institution'],
  ['specialty', 'education-specialty'],
  ['startDate', 'education-start'],
  ['endDate', 'education-end'],
] as const;

/** Add or edit one education entry. [HW-1008] */
export function EducationDialog({ open, onOpenChange, employeeId, entry }: EducationDialogProps) {
  const [form, setForm] = useState<EducationForm>(() => toEducationForm(entry));
  const [errors, setErrors] = useState<EducationErrors>({});
  const create = useCreateEducation(employeeId);
  const update = useUpdateEducation(employeeId);
  const isPending = create.isPending || update.isPending;
  const focusFirstError = useFocusFirstError(FIELD_IDS, errors, isPending);
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setForm(toEducationForm(entry));
    setErrors({});
  }, [open, entry]);

  const set = <K extends keyof EducationForm>(key: K, value: EducationForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const rest = withoutFieldError(prev, key);
      // "End date must not be before start date" sits on End date but can be
      // fixed from Start date too, so editing Start date clears it as well.
      return key === 'startDate' ? withoutFieldError(rest, 'endDate') : rest;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Read at submit: typing into an empty date input leaves its value '' and
    // fires no change, so only validity.badInput reveals a half-typed date.
    const found = validateEducation(form, {
      startDate: startRef.current?.validity.badInput,
      endDate: endRef.current?.validity.badInput,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError();
      return;
    }
    const input = toEducationInput(form);
    const callbacks = {
      onSuccess: () => {
        toast.success(entry ? 'Education updated' : 'Education added');
        onOpenChange(false);
      },
      onError: (err: unknown) => {
        // A 400 lands under the fields it names; only what names none is a toast.
        const mapped = serverFieldErrors(err, EDUCATION_SERVER_FIELDS);
        if (!mapped) {
          toast.error(extractErrorMessage(err, 'Failed to save education'));
          return;
        }
        setErrors(mapped.fields);
        focusFirstError();
        if (mapped.unmatched.length > 0) toast.error(mapped.unmatched.join('. '));
      },
    };
    if (entry) update.mutate({ id: entry.id, ...input }, callbacks);
    else create.mutate(input, callbacks);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{entry ? 'Edit Education' : 'Add Education'}</DialogTitle>
          <DialogDescription>A school, degree, course or certification.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="education-type">
                Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.type}
                onValueChange={(v) => set('type', v as EducationType)}
                disabled={isPending}
              >
                <SelectTrigger id="education-type" {...invalidProps('education-type', errors.type)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EDUCATION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {EDUCATION_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError id="education-type" error={errors.type} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="education-institution">
                Institution <span className="text-destructive">*</span>
              </Label>
              <Input
                id="education-institution"
                value={form.institution}
                onChange={(e) => set('institution', e.target.value)}
                disabled={isPending}
                className={errors.institution ? 'border-destructive' : ''}
                {...invalidProps('education-institution', errors.institution)}
              />
              <FieldError id="education-institution" error={errors.institution} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="education-specialty">Specialty</Label>
              <Input
                id="education-specialty"
                value={form.specialty}
                onChange={(e) => set('specialty', e.target.value)}
                disabled={isPending}
                className={errors.specialty ? 'border-destructive' : ''}
                {...invalidProps('education-specialty', errors.specialty)}
              />
              <FieldError id="education-specialty" error={errors.specialty} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="education-start">Start date</Label>
                <Input
                  id="education-start"
                  ref={startRef}
                  type="date"
                  value={form.startDate}
                  onChange={(e) => set('startDate', e.target.value)}
                  disabled={isPending}
                  className={errors.startDate ? 'border-destructive' : ''}
                  {...invalidProps('education-start', errors.startDate)}
                />
                <FieldError id="education-start" error={errors.startDate} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="education-end">End date</Label>
                <Input
                  id="education-end"
                  ref={endRef}
                  type="date"
                  value={form.endDate}
                  onChange={(e) => set('endDate', e.target.value)}
                  disabled={isPending}
                  className={errors.endDate ? 'border-destructive' : ''}
                  {...invalidProps('education-end', errors.endDate)}
                />
                {errors.endDate ? (
                  <FieldError id="education-end" error={errors.endDate} />
                ) : (
                  <p className="text-xs text-muted-foreground">Leave empty if still studying</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : entry ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
